import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { Turnstile } from '@marsidev/react-turnstile'
import { motion } from 'framer-motion'
import { Loader2, Send, LogIn } from 'lucide-react'
import { clearToken, setToken, post } from '@/utils/request'
import { getLoginCode } from '../services/auth'
import { notify } from '@/utils/crypto'
import { useModel } from '@/plugin-model/useModel'
import { useProfile } from '@/swr/useProfile'

// Shadcn UI 组件
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

const SITE_KEY = process.env.NEXT_PUBLIC_SITE_KEY

const SignIn = () => {
  const { viewSet } = useModel('view')
  const { mutate } = useProfile()
  const [checked, setChecked] = useState(true)
  const [codeSent, setCodeSent] = useState(false)
  const [clToken, setClToken] = useState('')
  const [countDown, setCountDown] = useState(60)
  const [btnText, setBtnText] = useState('发送验证码')
  const [loading, setLoading] = useState(false)

  const postLogin = async (params) => {
    const res = await post('v1/user/login', { ...params })
    return res
  }

  const login = async (params) => {
    setLoading(true)
    clearToken()
    const res = await postLogin(params)
    if (res.access_token) {
      setToken(res.access_token)
      viewSet('loginDialogOpen', false)
      viewSet('user_id', res.user.id)
      localStorage.setItem('user_id', res.user.id)
      mutate()
      setLoading(false)
    } else {
      notify(res.message, 'warning')
      setLoading(false)
    }
  }

  const { register, watch, handleSubmit, getValues } = useForm({
    mode: 'onBlur',
  })

  useEffect(() => {
    if (codeSent && countDown > 0) {
      setTimeout(() => setCountDown(countDown - 1), 1000)
      setBtnText(`${countDown} s`)
    } else if (countDown <= 0) {
      setBtnText('重新发送')
      setCodeSent(false)
      setCountDown(60)
    }
  }, [countDown, codeSent])

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <form
        onSubmit={handleSubmit((values) => {
          login({ account: values.account, code: values.code })
        })}
        className="space-y-4"
      >
        <div className="space-y-2">
          <Label htmlFor="account">手机号码</Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">+86</div>
            <Input id="account" className="h-11 pl-12 transition-all" autoComplete="off" autoFocus placeholder="请输入手机号码" {...register('account', { required: true })} />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="code">验证码</Label>
          <div className="flex items-center space-x-2">
            <Input id="code" autoComplete="off" className="h-11 transition-all" placeholder="请输入验证码" {...register('code', { required: true })} />
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="h-11 min-w-[110px] transition-all"
              disabled={countDown < 60 || codeSent}
              onClick={() => {
                if (watch('account'))
                  getLoginCode(getValues('account'), clToken).then((res) => {
                    if (res.code === 200) {
                      setCodeSent(true)
                      notify(res.message)
                    } else {
                      notify(res.message, 'warning')
                    }
                  })
              }}
            >
              {codeSent ? (
                <span className="text-xs">{btnText}</span>
              ) : (
                <>
                  <Send className="mr-1 h-4 w-4" /> {btnText}
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="py-2">
          <div className="grid grid-cols-1 gap-4">
            <Turnstile sitekey={SITE_KEY} onSuccess={setClToken} />
          </div>
        </div>

        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.2 }}>
          <Button type="submit" className="h-11 w-full" disabled={!checked || loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 登录中...
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4" /> 登录 / 注册
              </>
            )}
          </Button>
        </motion.div>

        <div className="flex items-center space-x-2 pt-2">
          <Checkbox id="terms" checked={checked} onCheckedChange={setChecked} className="data-[state=checked]:border-primary data-[state=checked]:bg-primary" />
          <Label htmlFor="terms" className="text-xs leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            我已阅读并同意
            <Link href="/agreement" className="ml-1 text-primary hover:underline">
              《Ground AI用户协议与隐私》
            </Link>
          </Label>
        </div>
      </form>
    </motion.div>
  )
}

export default SignIn
